/* Meama homepage personalization (rule-based, no AI). */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-meama-home-personalization]';
  var CONFIG_SELECTOR = '[data-meama-home-personalization-config]';
  var GRID_SELECTOR = '[data-meama-personalization-grid]';
  var SLIDER_SELECTOR = '[data-meama-personalization-slider]';
  var HEADING_SELECTOR = '[data-meama-personalization-heading]';

  var STORAGE_RECENT_PRODUCTS = 'meama_recent_products';
  var STORAGE_LAST_COLLECTION = 'meama_last_collection';
  var STORAGE_CAMPAIGN = 'meama_campaign_context_v1';
  var RECENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function now() {
    return Date.now ? Date.now() : new Date().getTime();
  }

  function log(config) {
    if (!config || !config.debug) return;
    var args = Array.prototype.slice.call(arguments, 1);
    args.unshift('[meama-personalization]');
    // eslint-disable-next-line no-console
    console.log.apply(console, args);
  }

  function safeJsonParse(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function safeGetStorage(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function isBlank(str) {
    return !str || !String(str).trim();
  }

  function normalizeHandle(handle) {
    if (!handle) return '';
    return String(handle).trim().toLowerCase();
  }

  function splitCsv(raw) {
    if (isBlank(raw)) return [];
    return String(raw)
      .split(',')
      .map(function (v) {
        return v.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function uniq(list) {
    var out = [];
    var seen = Object.create(null);
    for (var i = 0; i < list.length; i++) {
      var value = normalizeHandle(list[i]);
      if (!value) continue;
      if (seen[value]) continue;
      seen[value] = true;
      out.push(value);
    }
    return out;
  }

  function pushDataLayer(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign(
          {
            event: eventName
          },
          payload || {}
        )
      );
    } catch (e) {
      // no-op
    }
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      return res.json();
    });
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      return res.text();
    });
  }

  function readRecentViewedHandles(config) {
    var raw = safeGetStorage(STORAGE_RECENT_PRODUCTS);
    var parsed = safeJsonParse(raw);
    var cutoff = now() - RECENT_TTL_MS;

    if (!parsed) return [];

    // Backward compatible:
    // - v1: ["handle1","handle2",...]
    // - v2: [{handle:"...",ts:123}, ...]
    if (Array.isArray(parsed)) {
      var handles = [];
      for (var i = 0; i < parsed.length; i++) {
        var entry = parsed[i];
        if (!entry) continue;
        if (typeof entry === 'string') {
          handles.push(entry);
        } else if (typeof entry === 'object' && entry.handle) {
          if (entry.ts && entry.ts < cutoff) continue;
          handles.push(entry.handle);
        }
      }
      return uniq(handles);
    }

    log(config, 'recent products storage malformed, falling back', parsed);
    return [];
  }

  function readLastCollectionHandle() {
    return normalizeHandle(safeGetStorage(STORAGE_LAST_COLLECTION));
  }

  function readCampaignContext() {
    var parsed = safeJsonParse(safeGetStorage(STORAGE_CAMPAIGN));
    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  function matchesCampaign(mapping, ctx) {
    if (!mapping || !ctx) return false;
    var matchValue = String(mapping.matchValue || '').trim();
    if (!matchValue) return false;

    var lastTouch = ctx.lastTouch || ctx.firstTouch || null;
    if (!lastTouch) return false;

    if (mapping.matchType === 'utm_campaign' || mapping.matchType === 'utm_source' || mapping.matchType === 'utm_medium') {
      var key = mapping.matchType;
      var utm = (lastTouch.utm && typeof lastTouch.utm === 'object') ? lastTouch.utm : {};
      return String(utm[key] || '').toLowerCase() === matchValue.toLowerCase();
    }

    if (mapping.matchType === 'referrer_contains') {
      var ref = String(lastTouch.referrer || '').toLowerCase();
      return ref.indexOf(matchValue.toLowerCase()) !== -1;
    }

    if (mapping.matchType === 'landing_path_contains') {
      var path = String(lastTouch.landingPath || '').toLowerCase();
      return path.indexOf(matchValue.toLowerCase()) !== -1;
    }

    return false;
  }

  function collectionHandleForCategory(config, lastCollectionHandle) {
    var mappings = (config.mappings && config.mappings.category) || [];
    for (var i = 0; i < mappings.length; i++) {
      var m = mappings[i];
      if (!m || !m.sourceCollectionHandle) continue;
      if (normalizeHandle(m.sourceCollectionHandle) === normalizeHandle(lastCollectionHandle)) {
        return normalizeHandle(m.recommendedCollectionHandle || m.sourceCollectionHandle);
      }
    }
    return normalizeHandle(lastCollectionHandle);
  }

  function collectionHandleForUpsell(config, cart) {
    var mappings = (config.mappings && config.mappings.upsell) || [];
    var items = (cart && cart.items) || [];
    if (!items.length) return '';

    // Priority: first matching mapping by cart item product_type (contains match).
    // Supports comma-separated keywords in a single mapping (e.g. "machine, dropper").
    for (var i = 0; i < mappings.length; i++) {
      var m = mappings[i];
      if (!m || isBlank(m.productType) || isBlank(m.collectionHandle)) continue;
      var keywords = splitCsv(m.productType);
      if (!keywords.length) continue;
      for (var j = 0; j < items.length; j++) {
        var itemType = String(items[j].product_type || '').trim().toLowerCase();
        if (!itemType) continue;

        for (var k = 0; k < keywords.length; k++) {
          if (keywords[k] && itemType.indexOf(keywords[k]) !== -1) {
            return normalizeHandle(m.collectionHandle);
          }
        }
      }
    }

    return normalizeHandle(config.upsellDefaultCollectionHandle);
  }

  function isExcluded(config, product) {
    if (!product) return true;
    if (product.available === false) return true;

    var excludeHandles = splitCsv(config.excludeHandles);
    if (excludeHandles.length && product.handle && excludeHandles.indexOf(normalizeHandle(product.handle)) !== -1) return true;

    var excludeTags = splitCsv(config.excludeTags);
    if (excludeTags.length && Array.isArray(product.tags)) {
      for (var i = 0; i < product.tags.length; i++) {
        var tag = String(product.tags[i] || '').trim().toLowerCase();
        if (tag && excludeTags.indexOf(tag) !== -1) return true;
      }
    }
    return false;
  }

  function pickHandlesFromCollectionProducts(config, products, limit, excludedHandles) {
    var out = [];
    var excluded = excludedHandles || Object.create(null);
    for (var i = 0; i < products.length; i++) {
      if (out.length >= limit) break;
      var p = products[i];
      if (!p || !p.handle) continue;
      var handle = normalizeHandle(p.handle);
      if (!handle) continue;
      if (excluded[handle]) continue;
      if (isExcluded(config, p)) continue;
      excluded[handle] = true;
      out.push(handle);
    }
    return out;
  }

  function parseCardsFromHtml(html) {
    var container = document.createElement('div');
    container.innerHTML = html;
    var nodes = [];
    for (var i = 0; i < container.children.length; i++) {
      nodes.push(container.children[i]);
    }
    return nodes;
  }

  function initSectionEvents(section, config) {
    section.addEventListener(
      'click',
      function (e) {
        var link = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
        if (!link) return;
        try {
          var url = new URL(link.href, window.location.origin);
          var parts = url.pathname.split('/').filter(Boolean);
          var idx = parts.indexOf('products');
          var handle = idx > -1 ? parts[idx + 1] : '';
          pushDataLayer('meama_home_personalization_click', { product_handle: handle, href: link.href });
        } catch (err) {
          pushDataLayer('meama_home_personalization_click', { href: link.href });
        }
      },
      { passive: true }
    );

    section.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action^="/cart/add"], form[action="/cart/add"]')) return;
      var input = form.querySelector('input[name="id"]');
      pushDataLayer('meama_home_personalization_add_to_cart', { variant_id: input ? input.value : null });
    });
  }

  function setHeading(headingEl, config, rule) {
    if (!headingEl) return;
    var h = (config.headlines && config.headlines[rule]) || (config.headlines && config.headlines['default']) || '';
    if (!isBlank(h)) headingEl.textContent = h;
  }

  function maybeSchedule(fn) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      window.setTimeout(fn, 1);
    }
  }

  function run() {
    var section = document.querySelector(SECTION_SELECTOR);
    if (!section) return;

    var configNode = section.querySelector(CONFIG_SELECTOR);
    var grids = Array.prototype.slice.call(section.querySelectorAll(GRID_SELECTOR));
    var heading = section.querySelector(HEADING_SELECTOR);
    if (!configNode || !grids.length) return;

    var config = safeJsonParse(configNode.textContent) || {};
    config.productLimit = Number(config.productLimit || 8);

    initSectionEvents(section, config);

    var cardHtmlCache = new Map();
    var slider = section.querySelector(SLIDER_SELECTOR);
    if (slider) {
      slider.addEventListener('keydown', function (event) {
        // Match expected carousel keyboard behavior (like featured collections):
        // Left/Right arrows move the slider.
        var key = event.key || '';
        if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
        if (event.metaKey || event.altKey || event.ctrlKey) return;

        event.preventDefault();
        event.stopPropagation();

        slider.dispatchEvent(
          new CustomEvent(key === 'ArrowLeft' ? 'slider:previous' : 'slider:next', { bubbles: true, cancelable: true })
        );
      });
    }

    function fetchCard(handle) {
      var key = normalizeHandle(handle);
      if (!key) return Promise.resolve('');
      if (cardHtmlCache.has(key)) return cardHtmlCache.get(key);

      var promise = fetchText('/products/' + key + '?view=meama-card')
        .then(function (html) {
          return html || '';
        })
        .catch(function () {
          return '';
        });

      cardHtmlCache.set(key, promise);
      return promise;
    }

    function fetchCollectionProducts(handle, limit) {
      if (!handle) return Promise.resolve([]);
      var url = '/collections/' + handle + '/products.json?limit=' + String(limit || 50);
      return fetchJson(url)
        .then(function (data) {
          if (!data || !Array.isArray(data.products)) return [];
          return data.products;
        })
        .catch(function () {
          return [];
        });
    }

    function fetchProduct(handle) {
      if (!handle) return Promise.resolve(null);
      return fetchJson('/products/' + handle + '.js').catch(function () {
        return null;
      });
    }

    function fetchCart() {
      return fetchJson('/cart.js').catch(function () {
        return null;
      });
    }

    function renderHandles(rule, handles) {
      var finalHandles = uniq(handles).slice(0, config.productLimit);
      if (!finalHandles.length) return Promise.resolve(false);

      grids.forEach(function (grid) {
        grid.setAttribute('aria-busy', 'true');
      });
      setHeading(heading, config, rule);

      return Promise.all(
        finalHandles.map(function (h) {
          return fetchCard(h);
        })
      ).then(function (htmlParts) {
        var baseNodes = [];
        for (var i = 0; i < htmlParts.length; i++) {
          var html = htmlParts[i];
          if (!html) continue;
          baseNodes = baseNodes.concat(parseCardsFromHtml(html));
        }

        if (baseNodes.length) {
          grids.forEach(function (grid) {
            var clones = baseNodes.map(function (node) {
              return node.cloneNode(true);
            });
            grid.replaceChildren.apply(grid, clones);
          });
          pushDataLayer('meama_home_personalization_impression', { rule: rule, products: finalHandles });
          log(config, 'rendered rule', rule, finalHandles);
        }
        grids.forEach(function (grid) {
          grid.setAttribute('aria-busy', 'false');
        });

        if (slider && typeof slider.reset === 'function') {
          requestAnimationFrame(function () {
            try {
              slider.reset();
              if (typeof slider.updateButtons === 'function') slider.updateButtons();
              if (typeof slider.updateTabindex === 'function') slider.updateTabindex();
            } catch (e) {
              // no-op
            }
          });
        }
        return baseNodes.length > 0;
      });
    }

    function fillToLimit(primaryHandles, fillerHandles) {
      var limit = config.productLimit;
      var out = [];
      var seen = Object.create(null);

      function addMany(list) {
        for (var i = 0; i < list.length; i++) {
          if (out.length >= limit) break;
          var h = normalizeHandle(list[i]);
          if (!h || seen[h]) continue;
          seen[h] = true;
          out.push(h);
        }
      }

      addMany(primaryHandles || []);
      addMany(fillerHandles || []);
      return out;
    }

    function ruleCartUpsell() {
      return fetchCart().then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return null;

        var upsellCollection = collectionHandleForUpsell(config, cart);
        if (!upsellCollection) return null;

        var inCart = Object.create(null);
        for (var i = 0; i < cart.items.length; i++) {
          var handle = normalizeHandle(cart.items[i].handle);
          if (handle) inCart[handle] = true;
        }

        var limit = Math.max(50, config.productLimit * 6);
        return Promise.all([
          fetchCollectionProducts(upsellCollection, limit),
          fetchCollectionProducts(normalizeHandle(config.bestsellersCollectionHandle), limit)
        ]).then(function (results) {
          var upsellProducts = results[0] || [];
          var bestProducts = results[1] || [];

          var selected = pickHandlesFromCollectionProducts(config, upsellProducts, config.productLimit, inCart);
          if (selected.length < config.productLimit) {
            var filler = pickHandlesFromCollectionProducts(config, bestProducts, config.productLimit, inCart);
            selected = fillToLimit(selected, filler);
          }

          return { rule: 'cart', handles: selected };
        });
      });
    }

    function ruleContinueShopping() {
      var recent = readRecentViewedHandles(config);
      if (!recent.length) return Promise.resolve(null);

      var maxRecent = Math.min(4, recent.length);
      var recentShort = recent.slice(0, maxRecent);

      return Promise.all(
        recentShort.map(function (h) {
          return fetchProduct(h);
        })
      ).then(function (products) {
        var excluded = Object.create(null);
        var keep = [];
        for (var i = 0; i < products.length; i++) {
          var p = products[i];
          if (!p || !p.handle) continue;
          if (isExcluded(config, p)) continue;
          var handle = normalizeHandle(p.handle);
          if (!handle || excluded[handle]) continue;
          excluded[handle] = true;
          keep.push(handle);
        }

        var lastCollection = readLastCollectionHandle();
        var similarCollection = lastCollection ? collectionHandleForCategory(config, lastCollection) : '';
        var limit = Math.max(50, config.productLimit * 6);

        return Promise.all([
          fetchCollectionProducts(similarCollection, limit),
          fetchCollectionProducts(normalizeHandle(config.bestsellersCollectionHandle), limit)
        ]).then(function (results) {
          var similarProducts = results[0] || [];
          var bestProducts = results[1] || [];

          var similarHandles = pickHandlesFromCollectionProducts(config, similarProducts, config.productLimit, excluded);
          var filler = pickHandlesFromCollectionProducts(config, bestProducts, config.productLimit, excluded);

          var finalHandles = fillToLimit(keep.concat(similarHandles), filler);
          if (!finalHandles.length) return null;
          return { rule: 'continue', handles: finalHandles };
        });
      });
    }

    function ruleCategoryInterest() {
      var lastCollection = readLastCollectionHandle();
      if (!lastCollection) return Promise.resolve(null);

      var mapped = collectionHandleForCategory(config, lastCollection);
      var limit = Math.max(50, config.productLimit * 6);

      return Promise.all([
        fetchCollectionProducts(mapped, limit),
        fetchCollectionProducts(normalizeHandle(config.bestsellersCollectionHandle), limit)
      ]).then(function (results) {
        var products = results[0] || [];
        var bestProducts = results[1] || [];

        var excluded = Object.create(null);
        var selected = pickHandlesFromCollectionProducts(config, products, config.productLimit, excluded);
        if (selected.length < config.productLimit) {
          var filler = pickHandlesFromCollectionProducts(config, bestProducts, config.productLimit, excluded);
          selected = fillToLimit(selected, filler);
        }

        if (!selected.length) return null;
        return { rule: 'category', handles: selected };
      });
    }

    function ruleCampaignIntent() {
      var ctx = readCampaignContext();
      if (!ctx) return Promise.resolve(null);

      var mappings = (config.mappings && config.mappings.campaign) || [];
      var mappedCollection = '';
      for (var i = 0; i < mappings.length; i++) {
        if (matchesCampaign(mappings[i], ctx)) {
          mappedCollection = normalizeHandle(mappings[i].collectionHandle);
          break;
        }
      }
      if (!mappedCollection) return Promise.resolve(null);

      var limit = Math.max(50, config.productLimit * 6);
      return Promise.all([
        fetchCollectionProducts(mappedCollection, limit),
        fetchCollectionProducts(normalizeHandle(config.bestsellersCollectionHandle), limit)
      ]).then(function (results) {
        var products = results[0] || [];
        var bestProducts = results[1] || [];

        var excluded = Object.create(null);
        var selected = pickHandlesFromCollectionProducts(config, products, config.productLimit, excluded);
        if (selected.length < config.productLimit) {
          var filler = pickHandlesFromCollectionProducts(config, bestProducts, config.productLimit, excluded);
          selected = fillToLimit(selected, filler);
        }

        if (!selected.length) return null;
        return { rule: 'campaign', handles: selected };
      });
    }

    function ruleDefault() {
      var limit = Math.max(50, config.productLimit * 6);
      return fetchCollectionProducts(normalizeHandle(config.bestsellersCollectionHandle), limit).then(function (products) {
        var excluded = Object.create(null);
        var selected = pickHandlesFromCollectionProducts(config, products, config.productLimit, excluded);
        return { rule: 'default', handles: selected };
      });
    }

    function applyFirstMatch() {
      // Priority order (business rules):
      // 1) Cart-based upsell
      // 2) Continue shopping
      // 3) Category interest
      // 4) Campaign / intent
      // 5) Default
      return ruleCartUpsell()
        .then(function (res) {
          return res || ruleContinueShopping();
        })
        .then(function (res) {
          return res || ruleCategoryInterest();
        })
        .then(function (res) {
          return res || ruleCampaignIntent();
        })
        .then(function (res) {
          return res || ruleDefault();
        });
    }

    maybeSchedule(function () {
      applyFirstMatch().then(function (result) {
        if (!result || !result.handles || !result.handles.length) return;
        renderHandles(result.rule, result.handles);
      });
    });
  }

  run();
})();
